<template>
	<div
		class="g-header-images-preview"
		:class="[
			`is-style-${imageStyle}`,
			`is-display-${displayMode}`,
		]"
	>
		<template v-if="displayMode === 'tile'">
			<div
				class="g-header-images-preview__tile"
				:class="{ 'has-margin': imageStyle === 'margin' }"
			>
				<img
					v-for="(img, index) in items"
					:key="img.id"
					:src="img.previewUrl"
					class="g-header-images-preview__img"
					:class="{ 'is-previewable': previewable }"
					alt=""
					@click="onImageClick(index, $event)"
				/>
			</div>
		</template>
		<template v-else>
			<div
				ref="carousel"
				class="g-header-images-preview__carousel"
				:class="{ 'has-margin': imageStyle === 'margin' }"
				@touchstart.passive="onTouchStart"
				@touchmove="onTouchMove"
				@touchend="onTouchEnd"
				@touchcancel="onTouchEnd"
				@click="onCarouselClick"
			>
				<div
					class="g-header-images-preview__track"
					:class="{ 'is-dragging': isTouchDragging }"
					:style="trackStyle"
				>
					<div
						v-for="img in items"
						:key="img.id"
						class="g-header-images-preview__slide"
					>
						<img
							:src="img.previewUrl"
							class="g-header-images-preview__img"
							:class="{ 'is-previewable': previewable }"
							alt=""
						/>
					</div>
				</div>
				<div v-if="items.length > 1" class="g-header-images-preview__count">
					{{ activeIndex + 1 }}/{{ items.length }}
				</div>
				<div
					v-if="showDots"
					class="g-header-images-preview__dots"
				>
					<span
						v-for="(img, index) in items"
						:key="img.id"
						class="g-header-images-preview__dot"
						:class="{ 'is-active': index === activeIndex }"
						@click.stop="setActiveIndex(index)"
					></span>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
	const SWIPE_MIN_PX = 40;
	const SWIPE_RATIO = 0.18;

	export default {
		name: 'GHeaderImagesPreview',
		props: {
			items: {
				type: Array,
				default: () => [],
			},
			imageStyle: {
				type: String,
				default: 'full',
			},
			displayMode: {
				type: String,
				default: 'carousel',
			},
			previewable: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['preview'],
		data() {
			return {
				activeIndex: 0,
				touchStartX: 0,
				touchStartY: 0,
				isTouchDragging: false,
				dragOffset: 0,
				carouselWidth: 0,
				suppressClick: false,
			};
		},
		computed: {
			canSwipe() {
				return this.items.length > 1 && this.displayMode === 'carousel';
			},
			showDots() {
				return this.items.length > 1 && this.displayMode === 'carousel';
			},
			trackStyle() {
				const baseOffset = this.activeIndex * 100;
				if (!this.isTouchDragging || !this.carouselWidth) {
					return {
						transform: `translateX(-${baseOffset}%)`,
					};
				}
				return {
					transform: `translateX(calc(-${baseOffset}% + ${this.dragOffset}px))`,
				};
			},
		},
		watch: {
			items: {
				handler() {
					this.activeIndex = 0;
					this.resetTouchState();
				},
				deep: true,
			},
			displayMode() {
				this.activeIndex = 0;
				this.resetTouchState();
			},
		},
		methods: {
			setActiveIndex(index) {
				this.activeIndex = index;
			},
			getCarouselWidth() {
				return this.$refs.carousel?.offsetWidth || 0;
			},
			onImageClick(index, e) {
				if (!this.previewable) return;
				e?.stopPropagation?.();
				this.$emit('preview', index);
			},
			onCarouselClick(e) {
				if (!this.previewable) return;
				if (this.suppressClick) return;
				if (e?.target?.closest?.('.g-header-images-preview__dot')) return;
				e?.stopPropagation?.();
				this.$emit('preview', this.activeIndex);
			},
			onTouchStart(event) {
				if (!this.canSwipe) return;
				const touch = event.touches[0];
				this.touchStartX = touch.clientX;
				this.touchStartY = touch.clientY;
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.carouselWidth = this.getCarouselWidth();
			},
			onTouchMove(event) {
				if (!this.canSwipe) return;
				const touch = event.touches[0];
				const deltaX = touch.clientX - this.touchStartX;
				const deltaY = touch.clientY - this.touchStartY;
				if (!this.isTouchDragging) {
					if (Math.abs(deltaX) < 8) return;
					if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
					this.isTouchDragging = true;
				}
				event.preventDefault();
				let offset = deltaX;
				if (this.activeIndex === 0 && offset > 0) {
					offset *= 0.35;
				} else if (this.activeIndex === this.items.length - 1 && offset < 0) {
					offset *= 0.35;
				}
				this.dragOffset = offset;
			},
			onTouchEnd() {
				if (!this.canSwipe) {
					this.resetTouchState();
					return;
				}
				if (this.isTouchDragging) {
					this.suppressClick = true;
					const threshold = Math.max(
						SWIPE_MIN_PX,
						this.carouselWidth * SWIPE_RATIO,
					);
					if (this.dragOffset < -threshold && this.activeIndex < this.items.length - 1) {
						this.activeIndex += 1;
					} else if (this.dragOffset > threshold && this.activeIndex > 0) {
						this.activeIndex -= 1;
					}
					setTimeout(() => {
						this.suppressClick = false;
					}, 300);
				}
				this.resetTouchState();
			},
			resetTouchState() {
				this.isTouchDragging = false;
				this.dragOffset = 0;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-header-images-preview {
		width: 100%;
		overflow: hidden;
		margin-top: 0.32rem;
		&.is-style-full {
			border-radius: 0;
			margin-top: 0;
		}

		&.is-style-margin {
			.g-header-images-preview__tile.has-margin,
			.g-header-images-preview__carousel.has-margin {
				box-sizing: border-box;
			}
		}
	}

	.g-header-images-preview__tile {
		display: flex;
		flex-direction: column;
	}

	.g-header-images-preview__carousel {
		position: relative;
		overflow: hidden;
		width: 100%;
		touch-action: pan-y;
	}

	.g-header-images-preview__track {
		display: flex;
		align-items: flex-start;
		transition: transform 0.25s ease;

		&.is-dragging {
			transition: none;
		}
	}

	.g-header-images-preview__slide {
		flex: 0 0 100%;
		min-width: 0;
	}

	.g-header-images-preview__img {
		display: block;
		width: 100%;
		height: auto;
		object-fit: cover;

		&.is-previewable {
			cursor: pointer;
		}
	}

	.g-header-images-preview__count {
		position: absolute;
		right: 8px;
		bottom: 8px;
		padding: 2px 8px;
		border-radius: 10px;
		background: rgba(0, 0, 0, 0.45);
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
		pointer-events: none;
	}

	.g-header-images-preview__dots {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 5px;
	}

	.g-header-images-preview__dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.55);
		cursor: pointer;

		&.is-active {
			background: #285af9;
		}
	}
</style>
